<template>
  <div class="companies-container">
    <div class="container pt-4 pb-5">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="mb-0">Firma bearbeiten</h1>
        <div>
          <router-link to="/companies" class="btn btn-outline-secondary me-2">
            <i class="fas fa-arrow-left me-2"></i>Zurück
          </router-link>
        </div>
      </div>
      
      <div class="card shadow-sm">
        <div class="card-body p-4">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Lädt...</span>
            </div>
            <p class="mt-2">Daten werden geladen...</p>
          </div>
          
          <div v-else-if="error" class="alert alert-danger">
            {{ error }}
          </div>
          
          <form @submit.prevent="saveCompany" v-else>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="name" class="form-label fw-bold">Firmenname <span class="text-danger">*</span></label>
                  <input 
                    type="text" 
                    id="name" 
                    v-model="company.name" 
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.name }"
                    required
                  />
                  <div class="invalid-feedback" v-if="validationErrors.name">
                    {{ validationErrors.name[0] }}
                  </div>
                </div>
              </div>
              
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="email" class="form-label fw-bold">E-Mail <span class="text-danger">*</span></label>
                  <input 
                    type="email" 
                    id="email" 
                    v-model="company.email" 
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.email }"
                    required
                  />
                  <div class="invalid-feedback" v-if="validationErrors.email">
                    {{ validationErrors.email[0] }}
                  </div>
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="phone" class="form-label fw-bold">Telefon</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    v-model="company.phone" 
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.phone }"
                  />
                  <div class="invalid-feedback" v-if="validationErrors.phone">
                    {{ validationErrors.phone[0] }}
                  </div>
                </div>
              </div>
              
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="website" class="form-label fw-bold">Website</label>
                  <input 
                    type="url" 
                    id="website" 
                    v-model="company.website" 
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.website }"
                    placeholder="https://"
                  />
                  <div class="invalid-feedback" v-if="validationErrors.website">
                    {{ validationErrors.website[0] }}
                  </div>
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="street" class="form-label fw-bold">Straße</label>
                  <input 
                    type="text" 
                    id="street" 
                    v-model="company.street" 
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.street }"
                  />
                  <div class="invalid-feedback" v-if="validationErrors.street">
                    {{ validationErrors.street[0] }}
                  </div>
                </div>
              </div>
              
              <div class="col-md-3">
                <div class="form-group mb-3">
                  <label for="postal_code" class="form-label fw-bold">Postleitzahl</label>
                  <input 
                    type="text" 
                    id="postal_code" 
                    v-model="company.postal_code" 
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.postal_code }"
                  />
                  <div class="invalid-feedback" v-if="validationErrors.postal_code">
                    {{ validationErrors.postal_code[0] }}
                  </div>
                </div>
              </div>
              
              <div class="col-md-3">
                <div class="form-group mb-3">
                  <label for="city" class="form-label fw-bold">Stadt</label>
                  <input 
                    type="text" 
                    id="city" 
                    v-model="company.city" 
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.city }"
                  />
                  <div class="invalid-feedback" v-if="validationErrors.city">
                    {{ validationErrors.city[0] }}
                  </div>
                </div>
              </div>
            </div>
            
            <div class="row">
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="primary_color" class="form-label fw-bold">Primärfarbe</label>
                  <div class="input-group color-picker">
                    <span class="input-group-text">
                      <div class="color-preview" :style="{ backgroundColor: company.primary_color || '#007bff' }"></div>
                    </span>
                    <input 
                      type="text" 
                      id="primary_color" 
                      v-model="company.primary_color" 
                      class="form-control"
                      :class="{ 'is-invalid': validationErrors.primary_color }"
                      placeholder="#007bff"
                    />
                    <div class="invalid-feedback" v-if="validationErrors.primary_color">
                      {{ validationErrors.primary_color[0] }}
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="companyLogo" class="form-label fw-bold">Logo</label>
                  <div class="custom-file-upload">
                    <input 
                      type="file" 
                      class="custom-file-input" 
                      id="companyLogo" 
                      @change="handleLogoUpload"
                      accept="image/*"
                      :class="{ 'is-invalid': validationErrors.logo }"
                      ref="logoInput"
                    >
                    <label class="custom-file-label" for="companyLogo">
                      <span class="icon me-2"><i class="fas fa-upload"></i></span>
                      <span>
                        <span v-if="company.logo_url || logoPreview">{{ logoFileName || 'Logo ausgewählt' }}</span>
                        <span v-else>Keine ausgewählt</span>
                      </span>
                    </label>
                  </div>
                  <div class="invalid-feedback" v-if="validationErrors.logo">
                    <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                    {{ validationErrors.logo }}
                  </div>
                  <div class="d-flex align-items-center mt-2" v-if="logoPreview || company.logo_url">
                    <div class="preview-container me-3">
                      <img :src="logoPreview || company.logo_url" alt="Logo Vorschau" class="img-thumbnail logo-preview">
                    </div>
                    <button 
                      type="button" 
                      class="btn btn-outline-danger btn-sm"
                      @click="removeLogo"
                    >
                      <span class="icon me-1"><i class="fas fa-trash-alt"></i></span> 
                      Entfernen
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="row mt-3">
              <div class="col-md-12">
                <div class="form-group mb-3">
                  <label for="description" class="form-label fw-bold">Beschreibung</label>
                  <textarea 
                    id="description" 
                    v-model="company.description" 
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.description }"
                    rows="4"
                  ></textarea>
                  <div class="invalid-feedback" v-if="validationErrors.description">
                    {{ validationErrors.description[0] }}
                  </div>
                </div>
              </div>
            </div>
            
            <div class="card shadow-sm mt-4">
              <div class="card-header">
                <h5 class="mb-0">Administrator-Daten</h5>
              </div>
              <div class="card-body p-4">
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group mb-3">
                      <label for="adminName" class="form-label fw-bold">Name <span class="text-danger">*</span></label>
                      <input 
                        type="text" 
                        id="adminName" 
                        v-model="adminData.name" 
                        class="form-control"
                        :class="{ 'is-invalid': validationErrors['admin.name'] }"
                        required
                      />
                      <div class="invalid-feedback" v-if="validationErrors['admin.name']">
                        {{ validationErrors['admin.name'][0] }}
                      </div>
                    </div>
                  </div>
                  
                  <div class="col-md-6">
                    <div class="form-group mb-3">
                      <label for="adminEmail" class="form-label fw-bold">E-Mail <span class="text-danger">*</span></label>
                      <input 
                        type="email" 
                        id="adminEmail" 
                        v-model="adminData.email" 
                        class="form-control"
                        :class="{ 'is-invalid': validationErrors['admin.email'] }"
                        required
                      />
                      <div class="invalid-feedback" v-if="validationErrors['admin.email']">
                        {{ validationErrors['admin.email'][0] }}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="row">
                  <div class="col-md-12">
                    <div class="form-group mb-3">
                      <label for="adminPassword" class="form-label fw-bold">
                        Passwort 
                        <span class="text-muted">(leer lassen für keine Änderung)</span>
                      </label>
                      <input 
                        type="password" 
                        id="adminPassword" 
                        v-model="adminData.password" 
                        class="form-control"
                        :class="{ 'is-invalid': validationErrors['admin.password'] }"
                      />
                      <div class="invalid-feedback" v-if="validationErrors['admin.password']">
                        {{ validationErrors['admin.password'][0] }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="row mt-4">
              <div class="col-12 d-flex justify-content-between">
                <router-link to="/companies" class="btn btn-outline-secondary">
                  Abbrechen
                </router-link>
                
                <div>
                  <button type="button" class="btn btn-danger me-2" @click="confirmDelete" v-if="company.id">
                    <i class="fas fa-trash-alt me-2"></i>Löschen
                  </button>
                  
                  <button type="submit" class="btn btn-primary" :disabled="saving">
                    <span class="spinner-border spinner-border-sm me-2" v-if="saving"></span>
                    <i class="fas fa-save me-2" v-else></i>Speichern
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
    
    <!-- Löschbestätigung Modal -->
    <div class="modal fade" :class="{ show: showDeleteModal }" tabindex="-1" style="display: block;" v-if="showDeleteModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Firma löschen</h5>
            <button type="button" class="btn-close" @click="showDeleteModal = false"></button>
          </div>
          <div class="modal-body">
            <p>Sind Sie sicher, dass Sie die Firma <strong>{{ company.name }}</strong> löschen möchten?</p>
            <p class="text-danger">Diese Aktion kann nicht rückgängig gemacht werden und alle zugehörigen Daten gehen verloren.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showDeleteModal = false">Abbrechen</button>
            <button type="button" class="btn btn-danger" @click="deleteCompany" :disabled="deleting">
              <span class="spinner-border spinner-border-sm me-2" v-if="deleting"></span>
              <i class="fas fa-trash-alt me-2" v-else></i>Löschen bestätigen
            </button>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show"></div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useCompaniesStore } from '@/stores/companies';
import { useToast } from 'vue-toastification';
import http from '@/services/http';

export default defineComponent({
  name: 'CompanyEdit',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const companiesStore = useCompaniesStore();
    const toast = useToast();
    
    // Reactive state
    const company = reactive({
      id: null,
      name: '',
      email: '',
      phone: '',
      website: '',
      street: '',
      postal_code: '',
      city: '',
      description: '',
      primary_color: '#007bff',
      logo: null,
      logo_url: null
    });
    
    const adminData = reactive({
      name: '',
      email: '',
      password: ''
    });
    
    const loading = ref(true);
    const saving = ref(false);
    const deleting = ref(false);
    const error = ref(null);
    const validationErrors = ref({});
    const showDeleteModal = ref(false);
    const logoFile = ref(null);
    const logoFileName = ref('');
    const logoPreview = ref(null);
    
    // Computed properties
    const isEdit = computed(() => {
      return !!route.params.id;
    });
    
    // Methods
    const fetchCompanyData = async () => {
      try {
        const companyId = route.params.id;
        const response = await companiesStore.getCompany(companyId);
        
        // Prüfe, ob die Daten in der erwarteten Struktur vorliegen
        console.log('Geladene Firmendaten:', response);
        
        // Unpacken der Daten aus der Antwort
        if (response.company) {
          Object.assign(company, response.company);
          console.log('Firmendaten gesetzt:', company);
        } else if (typeof response === 'object' && !response.company) {
          // Fallback, falls die Daten direkt als Firmenobjekt zurückgegeben werden
          Object.assign(company, response);
          console.log('Firmendaten direkt gesetzt:', company);
        }
        
        // Admin-Daten laden, falls vorhanden
        if (response.admin) {
          adminData.name = response.admin.name;
          adminData.email = response.admin.email;
          console.log('Admin-Daten gesetzt:', adminData);
        }
        
        // Logo-URL verarbeiten, falls vorhanden
        if (company.logo_url) {
          console.log('Logo URL:', company.logo_url);
          
          // Extrahiere den Dateinamen aus der URL
          try {
            const url = new URL(company.logo_url);
            
            // Prüfe, ob der originalName-Parameter in der URL vorhanden ist
            if (url.searchParams.has('originalName')) {
              // Verwende den originalName-Parameter direkt
              logoFileName.value = url.searchParams.get('originalName');
              console.log('Originaler Dateiname aus URL-Parameter:', logoFileName.value);
            } else {
              // Fallback zur Pfad-Extraktion
              const pathParts = url.pathname.split('/');
              const encodedFilename = pathParts[pathParts.length - 1];
              
              // Versuche verschiedene Dekodierungsmethoden
              try {
                // Methode 1: Standard URL-Dekodierung
                logoFileName.value = decodeURIComponent(encodedFilename);
              } catch (e) {
                console.warn("Standard-Dekodierung fehlgeschlagen, versuche alternative Methoden");
                
                // Methode 2: Manuelles Ersetzen von %20 etc.
                logoFileName.value = encodedFilename.replace(/%([0-9A-F]{2})/g, (match, p1) => {
                  return String.fromCharCode(parseInt(p1, 16));
                });
              }
              
              // Bereinige den Dateinamen von möglichen Hash-Präfixen
              if (logoFileName.value.includes('_')) {
                const parts = logoFileName.value.split('_');
                if (parts.length > 1 && parts[0].length > 20) {
                  // Wenn der erste Teil wie ein Hash aussieht, entferne ihn
                  logoFileName.value = parts.slice(1).join('_');
                }
              }
              
              console.log('Extrahierter Dateiname aus Pfad:', logoFileName.value);
            }
          } catch (e) {
            console.error('Fehler beim Extrahieren des Dateinamens:', e);
            // Letzter Ausweg: Verwende den letzten Teil des Pfads ohne Dekodierung
            const parts = company.logo_url.split('/');
            logoFileName.value = parts[parts.length - 1].split('?')[0]; // Entferne Query-Parameter
          }
        }
        
        loading.value = false;
      } catch (err) {
        error.value = 'Fehler beim Laden der Firmendaten. Bitte versuchen Sie es später erneut.';
        loading.value = false;
        console.error('Fehler beim Laden der Firma:', err);
      }
    };
    
    const handleLogoUpload = (event) => {
      const file = event.target.files[0];
      if (!file) return;
      
      logoFile.value = file;
      logoFileName.value = file.name;
      
      // Vorschau des Logos anzeigen, aber nicht in company.logo speichern
      const reader = new FileReader();
      reader.onload = (e) => {
        logoPreview.value = e.target.result; // Vorschau in separatem Feld speichern
      };
      reader.readAsDataURL(file);
    };
    
    const saveCompany = async () => {
      saving.value = true;
      validationErrors.value = {};
      
      try {
        const formData = new FormData();
        
        // Füge alle Felder zum FormData hinzu
        Object.keys(company).forEach(key => {
          if (key !== 'logo' && key !== 'logo_url') {
            // Wichtig: Auch leere Werte müssen explizit gesendet werden
            formData.append(key, company[key] === null ? '' : company[key]);
          }
        });
        
        // Füge die Logo-Datei hinzu, falls vorhanden
        if (logoFile.value) {
          formData.append('logo', logoFile.value);
        } else if (!company.logo_url && !logoPreview.value) {
          formData.append('remove_logo', '1');
        }
        
        // Admin-Datenstruktur vorbereiten
        const adminDataToSend = {
          name: adminData.name,
          email: adminData.email
        };
        
        // Passwort nur senden, wenn es eingegeben wurde
        if (adminData.password) {
          adminDataToSend.password = adminData.password;
          adminDataToSend.password_confirmation = adminData.password;
        }
        
        // Admin-Daten in FormData hinzufügen (als JSON-String)
        formData.append('admin', JSON.stringify(adminDataToSend));
        
        // Bei PUT-Requests mit FormData muss _method=PUT angehängt werden
        formData.append('_method', 'PUT');
        
        const companyId = route.params.id;
        // Request direkt mit HTTP-Service senden
        const response = await http.post(`/api/admin/companies/${companyId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        toast.success('Firma erfolgreich aktualisiert!');
        
        // Nach dem Speichern zur Firmenliste zurückkehren
        router.push('/companies');
      } catch (error) {
        console.error('Fehler beim Speichern der Firma:', error);
        
        if (error.response && error.response.status === 422) {
          validationErrors.value = error.response.data.errors;
        } else {
          error.value = 'Fehler beim Aktualisieren der Firma. Bitte versuchen Sie es später erneut.';
        }
      } finally {
        saving.value = false;
      }
    };
    
    const confirmDelete = () => {
      showDeleteModal.value = true;
    };
    
    const deleteCompany = async () => {
      deleting.value = true;
      
      try {
        await companiesStore.deleteCompany(route.params.id);
        toast.success('Firma wurde erfolgreich gelöscht!');
        showDeleteModal.value = false;
        router.push('/companies');
      } catch (err) {
        error.value = 'Fehler beim Löschen der Firma. Bitte versuchen Sie es später erneut.';
        console.error('Fehler beim Löschen der Firma:', err);
      } finally {
        deleting.value = false;
      }
    };
    
    const removeLogo = () => {
      logoFile.value = null;
      logoFileName.value = '';
      logoPreview.value = null;
      company.logo_url = null;
    };
    
    // Lifecycle hooks
    onMounted(() => {
      if (isEdit.value) {
        fetchCompanyData();
      } else {
        loading.value = false;
      }
    });
    
    return {
      company,
      adminData,
      loading,
      saving,
      deleting,
      error,
      validationErrors,
      showDeleteModal,
      logoFile,
      logoFileName,
      logoPreview,
      isEdit,
      fetchCompanyData,
      handleLogoUpload,
      saveCompany,
      confirmDelete,
      deleteCompany,
      removeLogo
    };
  }
});
</script>

<style lang="scss" scoped>
.companies-container {
  .btn-primary {
    background: var(--primary-color);
    border-color: var(--primary-color);
    
    &:hover {
      background-color: var(--primary-color-dark);
      border-color: var(--primary-color-dark);
    }
  }
  
  .card {
    border-radius: var(--border-radius);
    overflow: hidden;
  }
  
  .form-label {
    margin-bottom: 0.5rem;
  }
  
  .form-control, .form-select {
    &:focus {
      border-color: var(--primary-color);
      box-shadow: 0 0 0 0.25rem rgba(var(--primary-color), 0.25);
    }
  }
  
  .tabs-wrapper {
    border-bottom: 1px solid var(--border-color);
    margin-bottom: 1.5rem;
  }
  
  .nav-tabs {
    border-bottom: none;
    
    .nav-link {
      margin-bottom: -1px;
      background: none;
      border: 1px solid transparent;
      border-top-left-radius: var(--border-radius);
      border-top-right-radius: var(--border-radius);
      color: var(--text-muted);
      padding: 0.75rem 1.25rem;
      font-weight: 500;
      
      &:hover {
        border-color: var(--border-color) var(--border-color) transparent;
        isolation: isolate;
      }
      
      &.active {
        color: var(--primary-color);
        background-color: var(--card-bg);
        border-color: var(--border-color) var(--border-color) var(--card-bg);
      }
    }
  }
  
  .page-title {
    margin-bottom: 1.5rem;
    font-weight: 700;
  }
}

.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.custom-file-upload {
  position: relative;
  overflow: hidden;
  display: inline-block;
  width: 100%;
  
  .custom-file-input {
    position: absolute;
    left: 0;
    top: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    z-index: 10;
  }
  
  .custom-file-label {
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);
    background-color: #fff;
    cursor: pointer;
    width: 100%;
    margin-bottom: 0;
    
    &:hover {
      background-color: var(--light-bg);
    }
  }
}

.logo-preview {
  max-height: 80px;
  max-width: 200px;
  object-fit: contain;
}

.preview-container {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: var(--border-radius);
  padding: 0.5rem;
}
</style> 